import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { uploadFile, getFiles, deleteFile } from '../firebase/storage';
import { createOrUpdateUser } from '../firebase/users';
import { logoutUser } from '../firebase/auth';
import UserSettings from './UserSettings'; // Import UserSettings component
import FileUploader from './FileUploader';
import FileList from './FileList';
import CommentCitationSystem from './CommentCitationSystem';

const FileUploadSystem = ({ user: propUser }) => {
  const [user, setUser] = useState(propUser || null);
  const [userData, setUserData] = useState(null);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('files');
  const [showSettings, setShowSettings] = useState(false); // New state for settings modal
  
  const auth = getAuth();
  
  // Handle auth state changes (if not provided via props)
  useEffect(() => {
    if (propUser) return;
    
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        setUser(authUser);
        try {
          const firestoreUser = await createOrUpdateUser(authUser);
          setUserData(firestoreUser);
          
          // Inlined loadFiles logic instead of calling the function
          setIsLoading(true);
          try {
            const filesData = await getFiles(authUser.uid);
            setFiles(filesData);
          } catch (fileErr) {
            console.error("Error loading files:", fileErr);
            setError("Failed to load files. Please refresh the page to try again.");
          } finally {
            setIsLoading(false);
          }
        } catch (err) {
          console.error("Error setting up user:", err);
          setError("Failed to set up user profile. Some features may be limited.");
        }
      } else {
        setUser(null);
        setUserData(null);
        setFiles([]);
        setIsLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, [auth, propUser]);
  
  // Load user data if provided via props
  useEffect(() => {
    if (propUser && !userData) {
      const loadUserData = async () => {
        try {
          const firestoreUser = await createOrUpdateUser(propUser);
          setUserData(firestoreUser);
          loadFiles();
        } catch (err) {
          console.error("Error setting up user:", err);
          setError("Failed to set up user profile. Some features may be limited.");
        }
      };
      
      loadUserData();
    }
  }, [propUser, userData]);
  
  // Load files
  const loadFiles = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const filesData = await getFiles(user.uid);
      setFiles(filesData);
    } catch (err) {
      console.error("Error loading files:", err);
      setError("Failed to load files. Please refresh the page to try again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle file upload
// In FileUploadSystem.js, modify the file upload handler:
const handleFileUpload = async (file) => {
  if (!user) return;
  
  try {
    await uploadFile(file, user.uid, user.displayName || user.email);
    loadFiles(); // Refresh the file list
  } catch (err) {
    console.error("Error uploading file:", err);
    setError("Failed to upload file. Please try again.");
  }
};


  
  // Handle file deletion
  const handleFileDelete = async (fileId) => {
    if (!user) return;
    
    try {
      await deleteFile(fileId, user.uid);
      loadFiles(); // Refresh the file list
    } catch (err) {
      console.error("Error deleting file:", err);
      setError("Failed to delete file. Please try again.");
    }
  };
  
 // And update the FileList component reference:
<FileList 
  onDeleteFile={handleFileDelete} 
  isLoading={isLoading}
  isAuthenticated={!!user}
  currentUserId={user?.uid}
/>

  // Handle user sign out
  const handleSignOut = async () => {
    try {
      await logoutUser();
    } catch (err) {
      console.error("Error signing out:", err);
      setError("Failed to sign out. Please try again.");
    }
  };
  
  // Toggle settings modal
  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };
  
  return (
    <div className="file-upload-system">
      {error && (
        <div className="error-banner">
          {error}
          <button 
            className="close-button" 
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Auth section */}
      <div className="auth-section">
        <div className="user-info">
          <div className="user-details">
            {user?.photoURL && (
              <img src={user.photoURL} alt={user.displayName} className="user-avatar" />
            )}
            <div>
              <p className="user-name">{user?.displayName || user?.email}</p>
              <p className="user-email">{user?.email}</p>
              {userData?.title && <p className="user-title">{userData.title}</p>}
            </div>
          </div>
          <div className="user-actions">
            <button 
              onClick={toggleSettings}
              className="settings-button"
              title="User Settings"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </button>
            <button 
              onClick={handleSignOut}
              className="sign-out-button"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
      
      {/* Tabs section */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'files' ? 'active' : ''}`}
          onClick={() => setActiveTab('files')}
        >
          Files
        </button>
        <button 
          className={`tab ${activeTab === 'comments' ? 'active' : ''}`}
          onClick={() => setActiveTab('comments')}
        >
          Comments & Citations
        </button>
      </div>
      
      {/* Content based on active tab */}
      <div className="tab-content">
        {activeTab === 'files' ? (
          <div className="files-section">
            <FileUploader onFileUpload={handleFileUpload} />
            <FileList 
              files={files} 
              onDeleteFile={handleFileDelete} 
              isLoading={isLoading}
              isAuthenticated={true}
            />
          </div>
        ) : (
          <CommentCitationSystem user={user} userData={userData} />
        )}
      </div>
      
      {/* User Settings Modal */}
      {showSettings && (
        <UserSettings onClose={toggleSettings} />
      )}
    </div>
  )};
  export default FileUploadSystem; 